import React from 'react'
import { isPrepaymentExpiringWithinMonth } from '@utils/prepaymentDateUtils'
import { inject, observer } from 'mobx-react'
import ChangeAgent from './components/ChangeAgent'
import ClosePaymentLate from '../PaymentLate/ClosePaymentLate'
import ViewCode from '@pages/Clients/ViewCode'
import ClientId from './components/ClientId'
import ClientPhone from './components/ClientPhone'
import Family from './components/Family'
import RelatedFamily from './components/RelatedFamily'
import moment from 'moment'
import Agent from './components/Agent'
import SubscriptionStatus from './components/SubscriptionStatus'
import SubscriptionType from './components/SubscriptionType'
import EditRoles from '@components/EditRoles'
import EditClientInfo from './components/EditClientInfo'
import FathersName from './components/FathersName'
import ClientInvoice from './components/ClientInvoice'
import { Tooltip, Spin, Button, Icon } from 'antd'
import PurchasePopup from './components/PurchasePopup'
import TranzilaPopup from './components/TranzilaPopup'
import { authStore } from 'store'
import { buildTranzilaProductDetails } from '@utils/purchasePopupUtils'

type ClientTopSectionState = {}

@inject((stores: any) => {
    const { authStore, subscriptionsStore, purchaseStore, popupStore } = stores
    return { authStore, subscriptionsStore, purchaseStore, popupStore }
})
@observer
class ClientTopSection extends React.Component<any, ClientTopSectionState> {
    private cardStatusFetchInProgress = false
    private lastFetchedClientId: number | string | null = null
    private isRedoPaymentForUnblock = false

    state: ClientTopSectionState = {}

    // Kick off the card status refresh as soon as the section appears.
    async componentDidMount() {
        await this.initializeCardStatus()
    }

    // Recompute the purchase prompt state whenever the client's subscription data changes.
    async componentDidUpdate(prevProps) {
        const prevClientInfo = prevProps.clientInfo
        const { clientInfo, popupStore } = this.props

        const prevClientId = prevClientInfo ? prevClientInfo.id : undefined
        const nextClientId = clientInfo ? clientInfo.id : undefined
        const prevSubscriptionStatus = prevClientInfo ? prevClientInfo.subscription_status : undefined
        const nextSubscriptionStatus = clientInfo ? clientInfo.subscription_status : undefined
        const prevSubscriptionId = prevClientInfo ? prevClientInfo.subscription_id : undefined
        const nextSubscriptionId = clientInfo ? clientInfo.subscription_id : undefined

        const clientChanged = prevClientId !== nextClientId
        const subscriptionStatusChanged = prevSubscriptionStatus !== nextSubscriptionStatus
        const subscriptionIdChanged = prevSubscriptionId !== nextSubscriptionId

        if (clientChanged || subscriptionStatusChanged || subscriptionIdChanged) {
            await this.initializeCardStatus()
        }
    }

    // Delegate fetching to the shared card status store so every consumer sees the same data.
    initializeCardStatus = async (): Promise<void> => {
        const { purchaseStore, clientInfo, subscriptionsStore } = this.props

        if (!clientInfo) {
            return
        }

        if (this.cardStatusFetchInProgress && this.lastFetchedClientId === clientInfo.id) {
            return
        }

        if (!subscriptionsStore.subscriptions || subscriptionsStore.subscriptions.length === 0) {
            await subscriptionsStore.getSubscriptions()
        }

        this.cardStatusFetchInProgress = true
        try {
            await purchaseStore.fetchRecentCardBlockedRow(clientInfo, subscriptionsStore.subscriptions)
            this.lastFetchedClientId = clientInfo.id
        } finally {
            this.cardStatusFetchInProgress = false
        }
    }

    // Check if redo prepayment button should be shown
    shouldShowRedoPrepaymentButton = (clientInfo: any) => {
        const { authStore, purchaseStore } = this.props
        const { roles } = authStore
        const safeRoles = roles || []

        if (!purchaseStore.cardStatusResolved) {
            return false
        }

        // Check role-based access
        const hasRequiredRole =
            safeRoles.includes('admin') ||
            safeRoles.includes('money') ||
            safeRoles.includes('agents_moderator') ||
            safeRoles.includes('distributors_manager')

        if (!clientInfo.prepayment_until) {
            return false
        }

        if (clientInfo.related_as != null || !hasRequiredRole || purchaseStore.userSubBlocked) {
            return false
        }

        return isPrepaymentExpiringWithinMonth(clientInfo.prepayment_until)
    }

    // Open Tranzila popup for redo prepayment with current product
    openRedoPaymentPopup = () => {
        const { clientInfo, subscriptionsStore, purchaseStore, popupStore } = this.props
        if (!clientInfo.subscription_id) return

        const currentSubscription = subscriptionsStore.subscriptions.find(
            (sub: any) => sub.id === clientInfo.subscription_id
        )
        if (!currentSubscription) return

        // Build properly formatted product details with all required fields
        const productDetails = buildTranzilaProductDetails(currentSubscription, null)
        ///productDetails.iteration_type = 'ONCE'
        // Set the selected product and open Tranzila popup
        purchaseStore.setSelectedTranzilaProduct(productDetails)
        popupStore.togglePopup('TranzilaPopup')
        return
    }

    // Open Tranzila popup for redo prepayment when unblocking with expired prepayment
    openRedoPaymentPopupForUnblock = () => {
        this.isRedoPaymentForUnblock = true
        this.openRedoPaymentPopup()
    }

    // Trigger unblock after successful redo-payment
    handleUnblockAfterPayment = () => {
        if (this.isRedoPaymentForUnblock) {
            this.isRedoPaymentForUnblock = false
        }
    }

    // Show details about the blocked subscription so the tooltip stays meaningful.
    getBlockedPurchaseTooltip = subscriptionsList => {
        const { purchaseStore } = this.props
        const recentRow = purchaseStore.getRecentCardBlockedRow()
        if (!recentRow || !recentRow['subscription_id']) {
            return null
        }
        const subscription = (subscriptionsList || []).find(
            subcription => subcription.id === recentRow['subscription_id']
        )

        if (!subscription) {
            return null
        }

        return (
            <>
                {subscription.name}
                <br />
                מחיר {recentRow['price']} ש״ח
            </>
        )
    }

    // Render the purchase prompt and quick-buy controls using the store state.
    renderPurchaseStatusCell = (subscriptionsList, roles) => {
        const { clientInfo, getClientInfo, purchaseStore } = this.props
        const lastProduct = purchaseStore.getLastProduct()
        const formattedDate = purchaseStore.getFormattedCardApprovalDate()
        // Purchase popup is hidden for client 47485.
        const showPurchasePrompt = Number(clientInfo.id) !== 47485 && purchaseStore.shouldShowPurchasePopup(roles)
        return (
            <div
                className="text-container"
                style={{
                    ...style.cell,
                    borderLeft: 'none',
                    marginLeft: '1rem',
                    borderRight: '1px solid',
                    height: 'min-content',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'start'
                }}
            >
                <Spin spinning={!purchaseStore.cardStatusResolved} size="default" className="purchase-status-spinner">
                    <span
                        className="text-inner"
                        style={{
                            marginLeft: '1rem',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'start',
                            gap: '6px',
                            height: 'min-content'
                        }}
                    >
                        {purchaseStore.cardStatusResolved && purchaseStore.cardCurrentlyBlocked && (
                            <Tooltip title={() => this.getBlockedPurchaseTooltip(subscriptionsList)}>
                                <strong className="card-currently-blocked" style={{ color: 'red' }}>
                                    חסום לרכישות באשראי
                                </strong>
                            </Tooltip>
                        )}
                        {purchaseStore.cardStatusResolved && purchaseStore.cardWasBlocked && (
                            <div
                                className="card-was-blocked"
                                style={{
                                    ...style.cell,
                                    borderLeft: 'none',
                                    width: '350px',
                                    marginLeft: '1rem',
                                    paddingRight: '0px',
                                    minHeight: '25px'
                                }}
                            >
                                <strong>{`שוחרר מחסימת רכישות באשראי בתאריך ${formattedDate}`}</strong>
                            </div>
                        )}

                        <div
                            className="buy-product-container"
                            style={{
                                ...style.cell,
                                borderLeft: 'none',
                                width: '300px',
                                paddingRight: '0px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'start'
                            }}
                        >
                            {clientInfo.related_as == null && showPurchasePrompt && (
                                <>
                                    <span
                                        style={{
                                            marginLeft: '1rem',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'start',
                                            gap: '6px'
                                        }}
                                    >
                                        <strong>{`רכישת מינוי:`}</strong>
                                    </span>
                                    <PurchasePopup
                                        productDetails={lastProduct}
                                        clientInfo={clientInfo}
                                        getClientInfo={getClientInfo}
                                        showTriggerButton={showPurchasePrompt}
                                    />
                                </>
                            )}
                        </div>
                    </span>
                </Spin>
            </div>
        )
    }

    // Render the top section layout and inject the purchase status controls.
    render() {
        const {
            clientInfo,
            getClientInfo,
            updateInfo,
            subscriptionsStore,
            loading_sto,
            purchaseStore,
            popupStore,
            onOpenStoDatesModal
        } = this.props
        const { id: authUserId } = authStore
        // roles can be null (e.g. during logout/re-auth) — guard before .includes.
        const roles = authStore.roles || []
        const { subscriptions } = subscriptionsStore
        const paymentLate = clientInfo ? clientInfo.subscription_payment_late : undefined
        const subscriptionId = clientInfo ? clientInfo.subscription_id : undefined
        const subscriptionStatus = clientInfo ? clientInfo.subscription_status : undefined
        const shouldShowPurchasePrompt = purchaseStore.shouldShowPurchasePopup(roles)

        const canSeeCollective = roles.includes('admin') || roles.includes('money')
        const showCollectivePrompt =
            canSeeCollective &&
            Number(clientInfo.id) !== 47485 &&
            clientInfo.related_as == null &&
            clientInfo.subscription_status !== 'ACTIVE' &&
            clientInfo.agent_is_collective

        const selectedTranzilaProduct = purchaseStore.getSelectedTranzilaProduct()
        return (
            <>
                <TranzilaPopup
                    clientInfo={clientInfo}
                    productDetails={selectedTranzilaProduct}
                    getClientInfo={getClientInfo}
                    onPaymentSuccess={this.handleUnblockAfterPayment}
                />
                <div style={{ ...style.wrapper }}>
                    <div style={{ ...style.line }}>
                        <div style={{ ...style.cell, width: '24%' }}>
                            <ClientId clientInfo={clientInfo} />
                        </div>
                        <div style={{ ...style.cell }}>
                            <ClientPhone clientInfo={clientInfo} />
                        </div>
                        <div style={{ ...style.cell }}>
                            <span style={{ marginLeft: '1rem' }}>
                                <strong>אימייל: </strong>
                                {clientInfo['email'] ? clientInfo['email'] : ''}
                            </span>
                        </div>
                        <div style={{ ...style.cell, borderLeft: 'none' }}>
                            <span style={{ marginLeft: '1rem' }}>
                                <strong>נוצר בתאריך: </strong>
                                {clientInfo['created_at'] ? moment(clientInfo['created_at']).format('DD/MM/YYYY') : ''}
                            </span>
                        </div>
                        {this.renderPurchaseStatusCell(subscriptions, roles)}
                    </div>

                    <div style={{ ...style.line }}>
                        <div
                            style={{ ...style.cell, width: '24%', display: 'flex', alignItems: 'center', gap: '10px' }}
                        >
                            <SubscriptionType
                                key={`${purchaseStore.getPurchaseStatusVersion()}-${
                                    shouldShowPurchasePrompt ? 'open' : 'closed'
                                }`}
                                clientInfo={clientInfo}
                                updateInfo={updateInfo}
                                subscriptions={subscriptions}
                                roles={roles}
                                loading_sto={loading_sto}
                            />
                            {showCollectivePrompt && (
                                <PurchasePopup
                                    collective
                                    productDetails={purchaseStore.getLastProduct()}
                                    clientInfo={clientInfo}
                                    getClientInfo={getClientInfo}
                                    showTriggerButton={showCollectivePrompt}
                                />
                            )}
                        </div>
                        <div style={{ ...style.cell }}>
                            <Family clientInfo={clientInfo} />
                        </div>
                        <div style={{ ...style.cell }}>
                            <RelatedFamily clientInfo={clientInfo} />
                        </div>
                        <div style={{ ...style.cell, display: 'flex' }}>
                            <Agent clientInfo={clientInfo} />
                            <ChangeAgent clientInfo={clientInfo} updateInfo={updateInfo} />
                        </div>
                        <div style={{ ...style.cell, borderLeft: 'none' }}>
                            <FathersName clientInfo={clientInfo} updateInfo={updateInfo} />
                        </div>
                    </div>

                    <div style={{ ...style.line }}>
                        {clientInfo.subscription_status === 'ACTIVE' &&
                            !clientInfo.parent_id &&
                            clientInfo.err_key === 'FILTER_MISMATCH' &&
                            clientInfo.err_message === 'dates mismatch' &&
                            clientInfo.mismatch &&
                            (roles.includes('admin') || roles.includes('money')) && (
                                <div style={{ marginLeft: '10px' }}>
                                    <Button onClick={onOpenStoDatesModal}>
                                        <Icon type="calendar" /> תיקון תאריך הו״ק
                                    </Button>
                                </div>
                            )}
                        <div style={{}}>
                            {clientInfo && clientInfo.phone && !roles.includes('viewer') ? (
                                <ViewCode phone={clientInfo.phone} />
                            ) : null}
                        </div>
                        {paymentLate && (
                            <div style={{ marginLeft: '10px' }}>
                                <ClosePaymentLate getClientInfo={getClientInfo} clientInfo={clientInfo} />
                            </div>
                        )}

                        {subscriptionId &&
                            ['ACTIVE', 'BLOCKED', 'AWAITING_BANK_TRANSFER'].includes(subscriptionStatus) && (
                                <div style={{ marginLeft: '10px' }}>
                                    <SubscriptionStatus
                                        clientInfo={clientInfo}
                                        getClientInfo={getClientInfo}
                                        roles={roles}
                                        onConfirm={this.props.onConfirm}
                                        loading_sto={loading_sto}
                                        onOpenRedoPayment={this.openRedoPaymentPopupForUnblock}
                                    />
                                </div>
                            )}

                        {this.shouldShowRedoPrepaymentButton(clientInfo) && (
                            <div style={{ marginLeft: '10px' }}>
                                <Button
                                    type="primary"
                                    onClick={this.openRedoPaymentPopup}
                                    style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                                >
                                    חידוש תשלום
                                </Button>
                            </div>
                        )}

                        {roles.includes('admin') && (
                            <div style={{ marginLeft: '10px' }}>
                                <EditClientInfo clientInfo={clientInfo} updateInfo={updateInfo} />
                            </div>
                        )}
                        {subscriptionId && !roles.includes('viewer') && (
                            <div style={{ overflowY: 'visible', maxHeight: '44px', marginLeft: '10px' }}>
                                <ClientInvoice client_id={clientInfo.id} user_id={authUserId} />
                            </div>
                        )}

                        <div style={{ overflowY: 'visible', maxHeight: '44px' }}>
                            {roles.includes('admin') && clientInfo.roles && (
                                <EditRoles userId={clientInfo.id} userRoles={clientInfo.roles} />
                            )}
                        </div>
                    </div>
                </div>
            </>
        )
    }
}

const style = {
    wrapper: { display: 'flex', flexDirection: 'column', fontSize: '16px' },
    line: { display: 'flex', width: '100%', marginBottom: '10px' },
    cell: { width: '18%', borderLeft: 'solid 1px', paddingRight: '10px' }
}

export default ClientTopSection
